import React, { Fragment, useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { LatLng, Map } from 'leaflet';
import { API } from 'aws-amplify';
import { connect, useDispatch, useSelector } from 'react-redux';
import { newNotification } from 'actions/uiActions';
import CloseIcon from '@mui/icons-material/Close';
import {
  acceptOrder,
  getOrder,
  rejectOrder,
  sendCustomerComm,
  setRouteConfirmationOrder,
  updateOrderStatus
} from 'actions/orderActions';
import { getBalbrigganData } from 'actions/routePlannerActions';
import { getCellName, selectFilteredOrders } from '../../../selectors/ordersSelector';
import { cellNameCoords, espooCellId } from '../../../constants';
import { getLatestJwtToken } from '../../../services/amplify-auth';
import { ORDER_RECEIVED } from '../../../actions/actionTypes';
import { getShortIdForOrder } from '../../../utils/orderUtils';
import { AddressFind, CreateDropLocation, DropLocation, Order } from '@m4a3/api-client';
import { Dialog, DialogPanel, Transition } from '@headlessui/react';
import { SuggestedDropPointConfirmationMap } from './SuggestedDropPointConfirmationMap';
import { logToDatadog } from '../../../services/datadog';
import { deliveryApi, orderfulApi } from '../../../services/api-instances';
import { AddressFindRemark } from '@m4a3/api-client/dist/models/address-find-remark.interface';
import SelectDropdown from '../../../components/SelectDropdown/SelectDropdown';
import { addressRemarkDropdownOptions } from '../../../utils/addressFindUtils';
import { validateDropLocation } from 'actions/adminActions';

interface SuggestedDropPointConfirmationModalProps {
  order?: Order;
  eircode: string;
  handleClose: (didCreateSuggestedDrops?: boolean) => void;
  notify: any;
  geofences: any;
  rejectOrder: any;
  updateOrderStatus: any;
  acceptOrder: any;
  obstacles: any
}
const SuggestedDropPointConfirmationModal = ({ order, eircode, handleClose, notify, geofences, rejectOrder, updateOrderStatus, acceptOrder, obstacles }: Readonly<SuggestedDropPointConfirmationModalProps>) => {
  const mapRef = useRef<Map>();
  const dispatch = useDispatch();
  const [orderToUse, setOrderToUse] = useState(order);

  const [addressInfo, setAddressInfo] = useState<AddressFind>();
  const [suggestedDropPoints, setSuggestedDropPoints] = useState<{ latLng: LatLng, isEvaluated: boolean; isLowRisk: boolean }[]>();
  const [dropPointIdToHighlight, setDropPointIdToHighlight] = useState<number>();
  const [addressRemarks, setAddressRemarks] = useState<AddressFindRemark[]>([]);
  const [addressRemarkText, setAddressRemarkText] = useState<string>('');
  const orders = useSelector(selectFilteredOrders);
  const cellName = useSelector(getCellName);
  const [riskEvaluated, setRiskEvaluated] = useState<string | null>(null);
  const [loadingRiskEvaluation, setLoadingRiskEvaluation] = useState<boolean>(false);
  const intialCoords: [number, number] = cellNameCoords[cellName];

  const hasInvalidDropPoints = (): boolean => {
    return !suggestedDropPoints || suggestedDropPoints.length === 0 || suggestedDropPoints.some(point => !point.isEvaluated || !point.isLowRisk);
  };
  
  
  const updateOrder = async () => {
    if (!orderToUse) return;
    try {
      const updatedOrder = await dispatch(getOrder(orderToUse.id));
      dispatch(setRouteConfirmationOrder(updatedOrder));
    } catch (err) {
      logToDatadog('error_getting_latest_order', {
        err,
        orderId: orderToUse?.id,
      })
    }
  };

  const getRemarksForAddress = async (addressFindId: string) => {
    orderfulApi.getRemarksForAddressfind(addressFindId)
      .then((remarks) => {
        setAddressRemarks(remarks as unknown as AddressFindRemark[]);
      });
  }

  const saveRemarkForAddress = async (addressFindId: string, message: string) => {
    orderfulApi.createAddressFindRemark(addressFindId, {
      message,
      isVisibleToCustomer: true,
      addressFindId
    })
  }

  useEffect(() => {
    updateOrder();
  }, [orderToUse?.id]);

  useEffect(() => {
    dispatch(getBalbrigganData());
  }, []);

  useEffect(() => {
    if (!orderToUse) return;
    const updatedOrder = orders.find(order => order.id === orderToUse.id);
    setOrderToUse(updatedOrder);
    return () => {};
  }, [orders]);

  const rejectOrderAndClose = async () => {
    if (!orderToUse) return;
    if (orderToUse.externalOrderId && orderToUse.externalProviderName === 'FLIPDISH') {
      // rejects the flipdish and manna orders
      await rejectOrder(orderToUse, 'drop_point_unsuitable');
    } else {
      await updateOrderStatus(orderToUse, 'REJECTED');
    }
    handleClose(false);
  };

  const invalidateEvaluatedRisk = () => {
    setRiskEvaluated(null);
  }

  const evaluateRisk = async () => {
    setRiskEvaluated(null);
    setLoadingRiskEvaluation(true);

    for (const dropPoint of suggestedDropPoints) {
      const risk = await validateDropLocation(dropPoint.latLng.lat, dropPoint.latLng.lng);
      dropPoint.isEvaluated = true;
      dropPoint.isLowRisk = risk === 'LOW';
    }

    setLoadingRiskEvaluation(false);
  }

  const fetchAddressInfo = async (eircode: string) => {
    if (!eircode) {
      return;
    }
    try {
      const address = await API.get('api', `/addressfind/locationId/${eircode}`, {});
      setAddressInfo(address);
      getRemarksForAddress(address.id);
      return address;
    } catch (error) {
      notify({
        variant: 'error',
        message: error.errorMessage
      });
    }
  };

  useEffect(() => {
    fetchAddressInfo(eircode);
  }, []);


  async function setIsPendingReviewToFalse() {
    const address: AddressFind = await fetchAddressInfo(eircode);
    return  API.put('api', `/addressfind/${address.id}`, {
      body: {
        ...address,
        isPendingReview: false
      }
    });
  }

  async function createDropLocationsForSuggestedDrops(suggestedDropPoints: { latLng: LatLng, isEvaluated: boolean }[]): Promise<void> {

    const validDropPoints = suggestedDropPoints.filter(point => point.isEvaluated);
    const promises = validDropPoints.map(async ({ latLng }: { latLng: LatLng }) => {
      const body: CreateDropLocation = {
        latitude: latLng.lat,
        longitude: latLng.lng,
        locationId: addressInfo.eircode,
        address: addressInfo.address,
        zoneId: 1,  // TODO: add some proper logic here or remove the need to have a zoneId
        isBackGardenApproved: true,
        userCreated: false,
        suggestedForAddressFindId: addressInfo.id,
        token: null,
        remarks: [], //If you add remarks here you will break the consumer app - Darragh King
      };

      if (addressInfo.cellId !== espooCellId) {
        return deliveryApi.saveDropLocation(body, false);
      }

      // Use admin-api instead of deliveryApi
      const token = await getLatestJwtToken();
      const adminUrl = process.env.REACT_APP_ADMIN_URL;
      const url = `${adminUrl}/drop-location`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return response.json();
    });
    await Promise.all(promises);
    if (validDropPoints.length > 0 && order) {
      const message = `Action needed for your order. Please open the app and select your preferred delivery spot`;
      await sendCustomerComm(order?.id, message);
    } 
  }


  const removeSuggestedDropPoint = async (cellId: number, dropLocation: DropLocation) => {

    if (cellId !== espooCellId) {
      return deliveryApi.updateDropLocation(dropLocation.id, {
        ...dropLocation,
        suggestedForAddressFindId: null,
      });
    }
    
    // Use admin-api instead of deliveryApi
    const token = await getLatestJwtToken();
    const adminUrl = process.env.REACT_APP_ADMIN_URL;
    const url = `${adminUrl}/drop-location/${dropLocation.id}`;
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...dropLocation,
        suggestedForAddressFindId: null,
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response.json();
  }

  const onAcceptButtonClicked = async () => {
    try {
      await createDropLocationsForSuggestedDrops(suggestedDropPoints);
  
      if (addressRemarkText) {
        await saveRemarkForAddress(addressInfo.id, addressRemarkText);
      }
  
      if (addressInfo.isPendingReview) {
        await setIsPendingReviewToFalse();
      }
      
      if (orderToUse) {
        if (order.status === 'CREATED') {
          acceptOrder(order, 0);
        }
      }
  
      handleClose(true);
    } catch (err) {
      notify({
        variant: 'error',
        message: err.errorMessage
      });
    }
  };
  
  const onChangeSuggestedDrops = (suggestedDrops: { latLng: LatLng, isEvaluated: boolean; isLowRisk: boolean; }[]) => {
    setSuggestedDropPoints(suggestedDrops); 
  };
  

  const getAcceptButtonCopy = (): string => {
    if (order?.status === 'CREATED') {
      return 'Add suggestions & accept'
    }

    return 'Add suggestions'
  };

  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-2000 max-h-screen" onClose={() => handleClose(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:m-8">

                <div className="sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <div style={{
                    height: 'calc(100vh - 100px)'
                  }}>
                    {(intialCoords && addressInfo) ? <SuggestedDropPointConfirmationMap
                      intialCoords={intialCoords}
                      mapRef={mapRef}
                      obstacles={obstacles}
                      dropPoints={addressInfo.dropLocationHistory}
                      onChangeSuggestedDrops={onChangeSuggestedDrops}
                      highlightOrderId={dropPointIdToHighlight}
                      geofences={geofences}
                      eircodeCoords={[addressInfo?.address_latitude, addressInfo?.address_longitude]}
                      loadingRiskEvaluation={loadingRiskEvaluation}
                    /> : <></>}
                  </div>
                  <div>
                    <Grid
                      container
                      flexBasis="space-between"
                      height={86}
                      style={{
                        width: '100%',
                        padding: 15,
                        borderBottom: '1px solid #D9DBE9'
                      }}>
                      <Grid item>
                        <h3
                          className={'text-lg font-semibold text-gray-800'}
                          style={{ marginTop: 3, marginBottom: 6 }}>
                          Suggest Drop Points
                        </h3>
                        <Grid container spacing={1}>
                          <Grid item>
                            {order ? <Typography variant="body2">
                              Order: {getShortIdForOrder(orderToUse)}
                            </Typography> : <></>}
                          </Grid>
                          <Grid item>
                            <Typography variant="body2">
                              {'・'}
                            </Typography>
                          </Grid>
                          <Grid item>
                            <Typography variant="body2">
                              Eircode: {eircode}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item flexGrow={1}>
                        <Button style={{ float: 'right' }} onClick={() => handleClose(false)}>
                          <CloseIcon></CloseIcon>
                        </Button>
                      </Grid>
                    </Grid>
                    <div>
                      <div className="border-t border-gray-100">
                        <dl className="divide-y divide-gray-100">
                          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-900">Address</dt>
                            <dd className="text-sm leading-6 text-gray-700 sm:col-span-2 sm:-mt-0.5">{addressInfo?.address || ''}</dd>
                          </div>
                          <div className="p-4">
                            <label htmlFor="drop-point-remark" className="block text-sm font-medium leading-6 text-gray-900">
                              If address is unsuitable, select a reason <span className='font-bold'>(visible to customer)</span>
                            </label>
                            <div className="mt-2">
                              <SelectDropdown
                                value={addressRemarkText}
                                onChange={(v: string) => setAddressRemarkText(v)}
                                options={addressRemarkDropdownOptions} />
                            </div>
                          </div>
                          <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-900">Past remarks</dt>
                            <dd className="text-sm leading-6 text-gray-700 sm:col-span-2 sm:-mt-0.5">
                              {addressRemarks?.length > 0 ? <ul className='flex-1 divide-y divide-gray-200 overflow-y-auto'>
                                {addressRemarks?.map((remark: AddressFindRemark) => {
                                  return (<li key={remark.id}>
                                      <div className='group relative flex items-center px-1 py-2 cursor-pointer hover:bg-gray-200'>
                                        {addressRemarkDropdownOptions.find(opt => opt.value === remark.message)?.displayText || remark.message}
                                      </div>
                                    </li>)
                                })}
                              </ul> : <span>None</span>}
                            </dd>
                          </div>
                          {addressInfo?.suggestedDropLocations?.length > 0 ? <div className="px-4 py-6 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                            <dt className="text-sm font-medium text-gray-900">Previously suggested drop locations</dt>
                            <dd className="text-sm leading-6 text-gray-700 sm:col-span-2 sm:-mt-0.5">
                              <ul className='flex-1 divide-y divide-gray-200 overflow-y-auto h-36'>
                                {addressInfo.suggestedDropLocations.map((suggestedDrop, index) => {
                                  return (<li key={suggestedDrop.id} onMouseEnter={() => {setDropPointIdToHighlight(suggestedDrop.id)}} onMouseLeave={() => {setDropPointIdToHighlight(null)}}>
                                      <div className='group relative flex justify-between px-1 py-2 cursor-pointer hover:bg-gray-200'>
                                        <span>Drop {index + 1} | {new Date(suggestedDrop.createDate).toLocaleString()}</span>
                                        <button
                                          type="button"
                                          onClick={async () => {
                                            await removeSuggestedDropPoint(addressInfo.cellId, suggestedDrop);
                                            fetchAddressInfo(eircode);
                                          }}
                                          className="inline rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                                        >
                                          Hide suggestion
                                        </button>
                                      </div>
                                    </li>);
                                })}
                              </ul>
                            </dd>
                          </div> : <></>}
                        </dl>
                      </div>
                    </div>
                    <div className="flex flex-row-reverse items-center pt-4 px-4">
                    <button
                      type="button"
                      disabled={hasInvalidDropPoints()}
                      className={`rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                        hasInvalidDropPoints() ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500 focus-visible:outline-blue-600'
                      }`}
                      onClick={onAcceptButtonClicked}>
                      {getAcceptButtonCopy()}
                    </button>
                      {order ? (
                        <>
                          <button
                            type="button"
                            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 mr-4"
                            onClick={rejectOrderAndClose}>
                            Reject
                          </button>
                          <button
                            type="button"
                            className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 mr-4"
                            onClick={evaluateRisk}
                            disabled={loadingRiskEvaluation}>
                            {loadingRiskEvaluation ? (
                              <div className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Evaluating...
                              </div>
                            ) : (
                              <>Evaluate (~10 seconds) {<span className='text-green-500'>{riskEvaluated}</span>}</>
                            )}
                          </button>
                          <div className="p-2 text-sm text-red-700">
                            Rejecting a drop point will reject and refund the customers order.
                          </div>
                        </> 
                      ) : (
                        <button
                          type="button"
                          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 mr-4"
                          onClick={evaluateRisk}
                          disabled={loadingRiskEvaluation}>
                          {loadingRiskEvaluation ? (
                            <div className="flex items-center">
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-900" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Evaluating...
                            </div>
                          ) : (
                            <>Evaluate (~10 seconds) {<span className='text-green-500'>{riskEvaluated}</span>}</>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};


const mapDispatchToProps = dispatch => {
  return {
    acceptOrder: (order, eta) => dispatch(acceptOrder(order, eta)),
    notify: (notification) => dispatch(newNotification(notification)),
    rejectOrder: (order: Order, reason: string) => dispatch(rejectOrder(order, reason)),
    updateOrderStatus: (order: Order, status: string) => dispatch(updateOrderStatus(order, status)),
    setSelectedOrder: (orderToUse) => {
      dispatch({
        type: ORDER_RECEIVED,
        payload: orderToUse
      });
    }
  };
};

export default connect(null, mapDispatchToProps)(SuggestedDropPointConfirmationModal);